/* eslint-disable max-lines */
import { CameraDetail } from '@hakimo-ui/hakimo/types';
import { SpecialZoneType, ZoneCanvas } from '@hakimo-ui/hakimo/ui-elements';
import {
  useCanUpdateActiveZone,
  useCanUpdateDeadZones,
  useCanUpdateVehicleParkingZone,
} from '@hakimo-ui/hakimo/util';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { DeleteZoneModal } from './DeleteZoneModal';
import { useZoneActions } from './useZoneActions';
import { useZoneMutations } from './useZoneMutations';
import { useZoneState } from './useZoneState';
import { ZoneToolbar } from './ZoneToolbar';

interface Props {
  camera: CameraDetail;
}

const ALL_ZONE_TYPES = new Set([
  SpecialZoneType.DeadZone,
  SpecialZoneType.VehicleParkingZone,
  SpecialZoneType.ActiveZone,
]);

export const SpecialZones = memo(function SpecialZonesV1({ camera }: Props) {
  const [visibleZoneTypes, setVisibleZoneTypes] =
    useState<Set<SpecialZoneType>>(ALL_ZONE_TYPES);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const canUpdateDeadZones = useCanUpdateDeadZones();
  const canUpdateVehicleParkingZone = useCanUpdateVehicleParkingZone();
  const canUpdateActiveZone = useCanUpdateActiveZone();

  const { saveZonesByType, isSaving, isDeleting } = useZoneMutations({
    cameraId: camera.id,
  });

  const {
    zones,
    setZones,
    canvasDimensions,
    imageResolution,
    initialZonesRef,
    initialZoneIdsRef,
    handleCanvasDimensionsChange,
    handleImageResolutionChange,
  } = useZoneState({ camera });

  const {
    selectedZoneId,
    handleZoneChange,
    handleSelectZone,
    handleAddZone,
    handleResetZone,
    handleSaveZone,
    handleDeleteZone,
  } = useZoneActions({
    zones,
    setZones,
    canvasDimensions,
    imageResolution,
    initialZonesRef,
    initialZoneIdsRef,
    saveZonesByType,
  });

  const handleToggleZoneType = useCallback((type: SpecialZoneType) => {
    setVisibleZoneTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);
  const confirmDelete = useCallback(() => {
    handleDeleteZone(closeDeleteModal);
  }, [handleDeleteZone, closeDeleteModal]);

  // Keyboard shortcut: Delete key to delete selected zone
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedZoneId) {
        e.preventDefault();
        openDeleteModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedZoneId, openDeleteModal]);

  const filteredZones = useMemo(
    () => zones.filter((zone) => visibleZoneTypes.has(zone.type)),
    [zones, visibleZoneTypes]
  );

  const selectedZone = useMemo(
    () => zones.find((zone) => zone.id === selectedZoneId),
    [zones, selectedZoneId]
  );

  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-1">
      <div className="overflow-hidden">
        <ZoneCanvas
          imageUrl={camera.imageUrl}
          zones={filteredZones}
          selectedZoneId={selectedZoneId}
          onZoneChange={handleZoneChange}
          onSelectZone={handleSelectZone}
          onCanvasDimensionsChange={handleCanvasDimensionsChange}
          onImageResolutionChange={handleImageResolutionChange}
          canUpdateDeadZones={canUpdateDeadZones}
          canUpdateVehicleParkingZone={canUpdateVehicleParkingZone}
          canUpdateActiveZone={canUpdateActiveZone}
        />
      </div>
      <ZoneToolbar
        visibleZoneTypes={visibleZoneTypes}
        onToggleZoneType={handleToggleZoneType}
        onAddZone={handleAddZone}
        selectedZoneId={selectedZoneId}
        selectedZoneType={selectedZone?.type}
        onResetZone={handleResetZone}
        onSaveZone={handleSaveZone}
        onDeleteZone={openDeleteModal}
        isSaving={isSaving}
        canUpdateDeadZones={canUpdateDeadZones}
        canUpdateVehicleParkingZone={canUpdateVehicleParkingZone}
        canUpdateActiveZone={canUpdateActiveZone}
      />
      <DeleteZoneModal
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
});

export default SpecialZones;
